/**
 * @file ManagerTasksPage.jsx
 * @location src/pages/ManagerTasksPage.jsx
 * @role 업무 페이지 (activeNav==='tasks') — 매니저 콕핏 (단일 뷰)
 *
 * 구성: [업무 할당] + 전체 표(상태[미완료/완료/전체] × 주목[안 읽음/기한 초과])
 *   + 하단 예정 업무 섹션(미래 지정일·TeamTaskTable 내부 분리) + 하단 루틴 업무.
 *   할당은 단일 버튼 — 일정(오늘/기한/지정일, 다음 영업일 칩)은 TaskModal Step1에서 선택. 검수 인박스는 razor ㉓ 폐기.
 */
import { useMemo } from 'react';
import { useApp } from '../store/AppContext';
import { activeOrClosedToday } from '../utils/taskSelectors';
import { getVisibleMemberIds, filterVisibleTasks } from '../utils/visibility';
import { getAttentionBuckets } from '../utils/managerAttention';
import { useCompleteConfirm, completeConfirmMsg } from '../hooks/useCompleteConfirm';
import { ConfirmModal } from '../components/ui/sharedui/ConfirmModal';
import { fmtLong, getTodayStr } from '../utils/dateUtils';
import { TeamTaskTable } from '../components/team/TeamTaskTable';
import { RecurringGroups } from '../components/tasks/RecurringGroups';
import { TasksFoot } from '../components/tasks/TasksFoot';
import { Icon } from '../components/ui/Icon';
import { EmptyCockpitCTA } from '../components/onboarding/EmptyCockpitCTA.jsx';

export function ManagerTasksPage({ onToast }) {
  const { S, act } = useApp();
  const templates = S.taskTemplates ?? [];

  // 업무 가시성 — 매니저=전사 (visibility 단일 진입점, razor ⑲)
  const all = useMemo(
    () => filterVisibleTasks(S.tasks, getVisibleMemberIds(S)),
    [S.tasks, S.members, S.uid]
  );
  // 「오늘 기준」 — 미종결은 항상, 완료/중지는 당일분만. 당일 지난 완료는 이력으로 빠짐.
  // 미래 지정일은 TeamTaskTable 가 하단 예정 업무 섹션으로 분리(그날 오면 위로).
  const today = getTodayStr();
  const filteredTasks = useMemo(() => activeOrClosedToday(all, today), [all, today]);
  const buckets = useMemo(() => getAttentionBuckets(all, S.uid), [all, S.uid]);
  const { requestToggle, confirmTask, onConfirm, onCancel } = useCompleteConfirm(onToast);

  return (
    <div className="content cockpit-wide">
      <div className="cockpit-top">
        <div>
          <h2 className="ptitle">업무</h2>
          <div className="psub">{fmtLong()}</div>
        </div>
        <div className="cockpit-top-actions">
          <button className="btns prout" onClick={() => act('SET_UI', { patch: { modalTask: true } })}>
            <Icon name="plus" size={15} /> 업무 할당
          </button>
        </div>
      </div>

      {(S.tasks ?? []).length === 0 ? (
        <EmptyCockpitCTA />
      ) : (
        <>
          <TeamTaskTable
            filteredTasks={filteredTasks}
            buckets={buckets}
            handleToggle={requestToggle}
            onToast={onToast}
          />

          <TasksFoot />
        </>
      )}

      {/* 하단 전폭 — 루틴업무 관리 */}
      <div className="routine-bottom">
        <div className="recur-section-head">
          <div className="recur-section-title"><Icon name="reset" size={16} /> 루틴 업무 할당</div>
          <button type="button" className="btns prout" onClick={() => act('SET_UI', { patch: { modalRoutine: true, routineModalId: null, routineModalMode: 'edit' } })}>
            <Icon name="plus" size={13} /> 루틴 업무 추가
          </button>
        </div>
        <RecurringGroups templates={templates} />
      </div>
      {confirmTask && (
        <ConfirmModal
          msg={completeConfirmMsg(confirmTask, today)}
          okLabel="완료로 처리"
          okClass="btnp"
          onOk={onConfirm}
          onCancel={onCancel}
        />
      )}
    </div>
  );
}
